// Version: 1.3
// v1.3: Inputs and selects take their look from index.css (input-sm for compact) (CV-50).
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v1.1: TrendBar container changed from height:60 to minHeight:60 so tall bars
//       (e.g. 40% click rate) no longer overflow upward over the section title.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';

const RAG_COLOR = { green: '#30d158', amber: '#e6b800', red: '#ff2d55', grey: '#86868b' };
const RAG_LABEL = { green: 'Low risk', amber: 'Moderate risk', red: 'High risk', grey: 'No data' };

function RagBadge({ rag }) {
  const color = RAG_COLOR[rag] || RAG_COLOR.grey;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      background: color + '22',
      color,
      border: `1px solid ${color}55`,
    }}>
      {RAG_LABEL[rag] || 'Unknown'}
    </span>
  );
}

function TrendBar({ campaigns }) {
  if (!campaigns || campaigns.length === 0) return null;
  const max = Math.max(...campaigns.map(c => c.click_rate || 0), 25);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        Click rate trend
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, minHeight: 60 }}>
        {campaigns.map((c, i) => {
          const h = max > 0 ? Math.max(4, Math.round((c.click_rate / max) * 56)) : 4;
          const color = RAG_COLOR[c.rag] || RAG_COLOR.grey;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{c.click_rate}%</div>
              <div style={{
                width: '100%',
                height: h,
                background: color,
                borderRadius: 3,
                opacity: 0.85,
                title: c.name,
              }} />
              <div style={{ fontSize: 9, color: 'var(--text-muted)', textAlign: 'center', maxWidth: 48, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.name}
              </div>
            </div>
          );
        })}
      </div>
      {/* Threshold lines legend */}
      <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        {[
          { color: RAG_COLOR.green, label: 'Below 10% — low risk' },
          { color: RAG_COLOR.amber, label: '10–25% — moderate' },
          { color: RAG_COLOR.red,   label: 'Above 25% — high risk' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-muted)' }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

function CampaignRow({ c }) {
  const launched = c.launch_date
    ? new Date(c.launch_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'
    : '—';
  const stats = c.stats || {};
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 90px 60px 60px 60px 80px',
      gap: 8,
      padding: '10px 0',
      borderBottom: '1px solid var(--border)',
      alignItems: 'center',
      fontSize: 12,
    }}>
      <div>
        <div style={{ fontWeight: 600, color: 'var(--text)' }}>{c.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{launched}</div>
      </div>
      <div><RagBadge rag={c.rag} /></div>
      <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{stats.sent ?? c.stats?.sent ?? '—'}</div>
      <div style={{ textAlign: 'center', color: RAG_COLOR[c.rag] }}>{stats.click_rate ?? '—'}%</div>
      <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{stats.open_rate ?? '—'}%</div>
      <div style={{ textAlign: 'center', color: '#30d158' }}>{stats.report_rate ?? '—'}%</div>
    </div>
  );
}

// ── Admin campaign creation form ──────────────────────────────────────────────

function CreateCampaignForm({ slug, onCreated }) {
  const [groups, setGroups]       = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [form, setForm] = useState({
    name: '',
    group_id: '',
    template_name: '',
    sender_name: 'IT Support',
    sender_email: '',
    launch_date: '',
  });

  useEffect(() => {
    Promise.all([
      authFetch(`/api/clients/${slug}/phishing/groups`).then(r => r.json()),
      authFetch(`/api/phishing/templates`).then(r => r.json()),
    ]).then(([g, t]) => {
      setGroups(Array.isArray(g) ? g : []);
      setTemplates(Array.isArray(t) ? t : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit() {
    if (!form.name || !form.group_id || !form.template_name || !form.sender_email) {
      setError('Please fill in all required fields.'); return;
    }
    setSaving(true); setError('');
    try {
      const r = await authFetch(`/api/clients/${slug}/phishing/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          group_id: parseInt(form.group_id, 10),
          launch_date: form.launch_date || null,
        }),
      });
      if (!r.ok) { const d = await r.json(); setError(d.error || 'Failed to create campaign'); }
      else { onCreated(); }
    } catch { setError('Request failed'); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: 13 }}>Loading groups and templates...</div>;

  const inputStyle = { width: '100%' };
  const labelStyle = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'block', fontWeight: 600 };

  return (
    <div style={{ marginTop: 16, padding: 16, background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: 'var(--text)' }}>New campaign</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Campaign name *</label>
          <input className="input-sm" style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Q3 Awareness Test" />
        </div>
        <div>
          <label style={labelStyle}>Recipient group *</label>
          <select className="input-sm" style={inputStyle} value={form.group_id} onChange={e => set('group_id', e.target.value)}>
            <option value="">Select group...</option>
            {groups.map(g => <option key={g.gophish_id} value={g.gophish_id}>{g.name} ({g.recipient_count} recipients)</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Email template *</label>
          <select className="input-sm" style={inputStyle} value={form.template_name} onChange={e => set('template_name', e.target.value)}>
            <option value="">Select template...</option>
            {templates.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Sender name</label>
          <input className="input-sm" style={inputStyle} value={form.sender_name} onChange={e => set('sender_name', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Sender email *</label>
          <input className="input-sm" style={inputStyle} value={form.sender_email} onChange={e => set('sender_email', e.target.value)} placeholder="itsupport@clientdomain.com" />
        </div>
        <div>
          <label style={labelStyle}>Launch date (leave blank to launch immediately)</label>
          <input className="input-sm" style={inputStyle} type="datetime-local" value={form.launch_date} onChange={e => set('launch_date', e.target.value)} />
        </div>
      </div>
      {error && <div style={{ marginTop: 10, color: '#ff2d55', fontSize: 12 }}>{error}</div>}
      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <button className="btn btn-primary"
          onClick={submit}
          disabled={saving}
         
        >
          {saving ? 'Launching...' : 'Launch campaign'}
        </button>
      </div>
    </div>
  );
}

// ── Main PhishingSection ──────────────────────────────────────────────────────

export default function PhishingSection({ slug, isAdmin }) {
  const [summary, setSummary]         = useState(null);
  const [campaigns, setCampaigns]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [syncing, setSyncing]         = useState(null);
  const [showCreate, setShowCreate]   = useState(false);
  const [error, setError]             = useState('');

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([
      authFetch(`/api/clients/${slug}/phishing/summary`).then(r => r.json()),
      authFetch(`/api/clients/${slug}/phishing/campaigns`).then(r => r.json()),
    ]).then(([s, c]) => {
      setSummary(s);
      setCampaigns(Array.isArray(c) ? c : []);
      setLoading(false);
    }).catch(() => { setError('Could not load phishing data'); setLoading(false); });
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  async function syncCampaign(campaignId) {
    setSyncing(campaignId);
    await authFetch(`/api/clients/${slug}/phishing/campaigns/${campaignId}/sync`, { method: 'POST' });
    setSyncing(null);
    load();
  }

  async function deleteCampaign(campaignId, name) {
    if (!window.confirm(`Delete campaign "${name}"? This cannot be undone.`)) return;
    await authFetch(`/api/clients/${slug}/phishing/campaigns/${campaignId}`, { method: 'DELETE' });
    load();
  }

  if (loading) return (
    <div className="card" style={{ marginTop: 16, padding: 24, textAlign: 'center' }}>
      <span className="spinner" />
    </div>
  );

  if (error) return null;

  // If not configured and not admin, hide entirely
  if (!summary?.configured && !isAdmin) return null;

  const latestRag = summary?.rag || 'grey';
  const ragColor  = RAG_COLOR[latestRag];

  return (
    <div className="card" style={{ marginTop: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            Phishing Simulation
          </div>
          {summary?.configured && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
              {summary.total_campaigns} campaign{summary.total_campaigns !== 1 ? 's' : ''} run
              {summary.latest_launch_date && ` · Last: ${new Date(summary.latest_launch_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'}`}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {summary?.configured && <RagBadge rag={latestRag} />}
          {isAdmin && (
            <button className="btn btn-secondary btn-sm"
              onClick={() => setShowCreate(v => !v)}
             
            >
              {showCreate ? 'Cancel' : '+ New campaign'}
            </button>
          )}
        </div>
      </div>

      {/* Not yet configured state */}
      {!summary?.configured && isAdmin && (
        <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          No campaigns have been run for this client yet.
        </div>
      )}

      {/* Trend chart */}
      {summary?.configured && summary.trend?.length > 0 && (
        <TrendBar campaigns={summary.trend} />
      )}

      {/* Latest click rate stat */}
      {summary?.configured && summary.latest_click_rate !== null && summary.latest_click_rate !== undefined && (
        <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: ragColor, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
              {summary.latest_click_rate}%
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>latest click rate</div>
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)', fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
              {summary.total_campaigns}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>campaigns run</div>
          </div>
        </div>
      )}

      {/* Campaign list */}
      {campaigns.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>
            Campaigns
          </div>
          {/* Column headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isAdmin ? '1fr 90px 60px 60px 60px 80px 80px' : '1fr 90px 60px 60px 60px 80px',
            gap: 8, padding: '6px 0',
            borderBottom: '1px solid var(--border)',
            fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            <div>Campaign</div>
            <div>Status</div>
            <div style={{ textAlign: 'center' }}>Sent</div>
            <div style={{ textAlign: 'center' }}>Clicked</div>
            <div style={{ textAlign: 'center' }}>Opened</div>
            <div style={{ textAlign: 'center' }}>Reported</div>
            {isAdmin && <div style={{ textAlign: 'center' }}>Actions</div>}
          </div>
          {campaigns.map(c => (
            <div key={c.id} style={{
              display: 'grid',
              gridTemplateColumns: isAdmin ? '1fr 90px 60px 60px 60px 80px 80px' : '1fr 90px 60px 60px 60px 80px',
              gap: 8, padding: '10px 0',
              borderBottom: '1px solid var(--border)',
              alignItems: 'center', fontSize: 12,
            }}>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text)' }}>{c.name}</div>
                {c.launch_date && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {new Date(c.launch_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' GMT'}
                  </div>
                )}
              </div>
              <div><RagBadge rag={c.rag} /></div>
              <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{c.stats?.sent ?? '—'}</div>
              <div style={{ textAlign: 'center', color: RAG_COLOR[c.rag] }}>{c.stats?.click_rate ?? '—'}%</div>
              <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{c.stats?.open_rate ?? '—'}%</div>
              <div style={{ textAlign: 'center', color: '#30d158' }}>{c.stats?.report_rate ?? '—'}%</div>
              {isAdmin && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => syncCampaign(c.id)}
                    disabled={syncing === c.id}
                    title="Sync results from GoPhish"
                   
                  >
                    {syncing === c.id ? '...' : '↻'}
                  </button>
                  <button className="btn btn-danger btn-sm"
                    onClick={() => deleteCampaign(c.id, c.name)}
                    title="Delete campaign"
                   
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create campaign form */}
      {isAdmin && showCreate && (
        <CreateCampaignForm
          slug={slug}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}
